import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/seo/seo-head";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Download, TrendingUp, TrendingDown, PiggyBank, BarChart3, Calendar } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";
import {
  exportMonthlyReportToPDF,
  exportIncomeExpenseToCSV,
  exportCategoryBreakdownToCSV,
  exportNetWorthReportToPDF,
} from "@/lib/export";
import { useLanguage } from "@/contexts/language-context";

interface MonthlyReport {
  period: string;
  income: {
    total: number;
    count: number;
    average: number;
    bySource: Array<{ sourceName: string; total: number; count: number }>;
  };
  expenses: {
    total: number;
    count: number;
    average: number;
    byCategory: Array<{ category: string; total: number; count: number }>;
  };
  savings: {
    amount: number;
    rate: number;
  };
}

interface IncomeExpenseReport {
  period: {
    start: string;
    end: string;
  };
  income: {
    total: number;
    count: number;
    average: number;
    bySource: Array<{ sourceName: string; total: number; count: number }>;
  };
  expenses: {
    total: number;
    count: number;
    average: number;
    byCategory: Array<{ category: string; total: number; count: number }>;
  };
  comparison: {
    difference: number;
    savingsRate: number;
    incomeVsExpenseRatio: number;
  };
}

interface CategoryBreakdownReport {
  period: {
    start: string;
    end: string;
  };
  incomeBySource: Array<{
    sourceName: string;
    total: number;
    count: number;
    percentage: number;
  }>;
  expensesByCategory: Array<{
    category: string;
    total: number;
    count: number;
    percentage: number;
  }>;
  totals: {
    income: number;
    expenses: number;
    savings: number;
  };
}

export function ReportsPage() {
  const { formatCurrency } = useCurrency();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<"monthly" | "net-worth" | "income-expense" | "category-breakdown">("monthly");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monthly report state
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReport | null>(null);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState(new Date().getMonth() + 1);

  // Income-Expense report state
  const [incomeExpenseReport, setIncomeExpenseReport] = useState<IncomeExpenseReport | null>(null);
  const [ieStartDate, setIeStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]
  );
  const [ieEndDate, setIeEndDate] = useState(new Date().toISOString().split("T")[0]);

  // Category breakdown report state
  const [categoryReport, setCategoryReport] = useState<CategoryBreakdownReport | null>(null);
  const [cbStartDate, setCbStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]
  );
  const [cbEndDate, setCbEndDate] = useState(new Date().toISOString().split("T")[0]);

  // Net worth report state
  const [netWorthReport, setNetWorthReport] = useState<{
    current: {
      netWorth: number;
      totalAssets: number;
      totalDebts: number;
      date: string;
    };
    change: {
      netWorth: number;
      percentage: number;
      assets: number;
      debts: number;
    };
  } | null>(null);

  const fetchMonthlyReport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<{ report: MonthlyReport }>(
        `/reports/monthly?year=${monthlyYear}&month=${monthlyMonth}`
      );
      setMonthlyReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch monthly report");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchIncomeExpenseReport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<{ report: IncomeExpenseReport }>(
        `/reports/income-expense?startDate=${ieStartDate}&endDate=${ieEndDate}`
      );
      setIncomeExpenseReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch income-expense report");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoryBreakdownReport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<{ report: CategoryBreakdownReport }>(
        `/reports/category-breakdown?startDate=${cbStartDate}&endDate=${cbEndDate}`
      );
      setCategoryReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch category breakdown report");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNetWorthReport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<{
        report: {
          current: {
            netWorth: number;
            totalAssets: number;
            totalDebts: number;
            date: string;
          };
          change: {
            netWorth: number;
            percentage: number;
            assets: number;
            debts: number;
          };
        };
      }>("/reports/net-worth");
      setNetWorthReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch net worth report");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "monthly") {
      fetchMonthlyReport();
    } else if (activeTab === "income-expense") {
      fetchIncomeExpenseReport();
    } else if (activeTab === "category-breakdown") {
      fetchCategoryBreakdownReport();
    } else if (activeTab === "net-worth") {
      fetchNetWorthReport();
    }
  }, [activeTab]);

  return (
    <>
      <SEOHead
        title="Reports - Personal Finance App"
        description="Generate detailed financial reports including monthly reports, income vs expenses analysis, category breakdowns, and net worth reports."
        keywords="financial reports, monthly report, income report, expense report, financial analysis, budget report"
        canonicalUrl="/reports"
      />
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("reports.title")}</h1>
        <p className="text-muted-foreground">
          {t("reports.subtitle")}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-muted/50 rounded-xl overflow-x-auto no-scrollbar">
        <Button
          variant={activeTab === "monthly" ? "default" : "ghost"}
          className={`rounded-lg whitespace-nowrap shrink-0 ${activeTab === "monthly" ? "shadow-sm" : ""}`}
          onClick={() => setActiveTab("monthly")}
        >
          {t("reports.monthlyReport")}
        </Button>
        <Button
          variant={activeTab === "net-worth" ? "default" : "ghost"}
          className={`rounded-lg whitespace-nowrap shrink-0 ${activeTab === "net-worth" ? "shadow-sm" : ""}`}
          onClick={() => setActiveTab("net-worth")}
        >
          {t("reports.netWorthReport")}
        </Button>
        <Button
          variant={activeTab === "income-expense" ? "default" : "ghost"}
          className={`rounded-lg whitespace-nowrap shrink-0 ${activeTab === "income-expense" ? "shadow-sm" : ""}`}
          onClick={() => setActiveTab("income-expense")}
        >
          {t("reports.incomeVsExpenses")}
        </Button>
        <Button
          variant={activeTab === "category-breakdown" ? "default" : "ghost"}
          className={`rounded-lg whitespace-nowrap shrink-0 ${activeTab === "category-breakdown" ? "shadow-sm" : ""}`}
          onClick={() => setActiveTab("category-breakdown")}
        >
          {t("reports.categoryBreakdown")}
        </Button>
      </div>

      {error && (
        <div className="rounded-lg bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">{t("reports.loadingReport")}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Monthly Report */}
          {activeTab === "monthly" && monthlyReport && (
            <div className="space-y-6">
              {/* Controls */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        type="number"
                        value={monthlyYear}
                        onChange={(e) => setMonthlyYear(parseInt(e.target.value, 10))}
                        className="w-20 sm:w-24"
                        min="2000"
                        max="2100"
                      />
                      <Select value={monthlyMonth.toString()} onValueChange={(v) => setMonthlyMonth(parseInt(v, 10))}>
                        <SelectTrigger className="w-full sm:w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                            <SelectItem key={month} value={month.toString()}>
                              {new Date(2000, month - 1).toLocaleString(language === "id" ? "id-ID" : "en-US", { month: "long" })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={fetchMonthlyReport} className="flex-1 sm:flex-none">{t("reports.generate")}</Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportMonthlyReportToPDF(monthlyReport)}
                        className="flex-1 sm:flex-none"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">{t("reports.exportPDF")}</span>
                        <span className="sm:hidden">PDF</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <TrendingUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.totalIncome")}</p>
                        <p className="text-xl font-bold text-primary tabular-nums font-mono">
                          {formatCurrency(monthlyReport.income.total)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                        <TrendingDown className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.totalExpenses")}</p>
                        <p className="text-xl font-bold text-destructive tabular-nums font-mono">
                          {formatCurrency(monthlyReport.expenses.total)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-chart-2/10 flex items-center justify-center shrink-0">
                        <PiggyBank className="h-5 w-5 text-chart-2" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.savings")}</p>
                        <p className="text-xl font-bold tabular-nums font-mono">
                          {formatCurrency(monthlyReport.savings.amount)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-chart-1/10 flex items-center justify-center shrink-0">
                        <BarChart3 className="h-5 w-5 text-chart-1" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.savingsRate")}</p>
                        <p className="text-xl font-bold tabular-nums font-mono">
                          {monthlyReport.savings.rate.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Breakdowns */}
              <div className="grid gap-4 md:gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm md:text-base flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                      {t("reports.incomeBySource")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {monthlyReport.income.bySource.map((source) => (
                        <div key={source.sourceName} className="flex items-center justify-between p-2 md:p-3 rounded-lg bg-muted/50">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{source.sourceName}</p>
                            <p className="text-[10px] md:text-xs text-muted-foreground">{source.count} transactions</p>
                          </div>
                          <span className="font-medium tabular-nums font-mono text-sm md:text-base text-primary shrink-0 ml-2">
                            {formatCurrency(source.total)}
                          </span>
                        </div>
                      ))}
                      {monthlyReport.income.bySource.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No income data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm md:text-base flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-destructive" />
                      {t("reports.expensesByCategory")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {monthlyReport.expenses.byCategory.map((category) => (
                        <div key={category.category} className="flex items-center justify-between p-2 md:p-3 rounded-lg bg-muted/50">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{category.category}</p>
                            <p className="text-[10px] md:text-xs text-muted-foreground">{category.count} transactions</p>
                          </div>
                          <span className="font-medium tabular-nums font-mono text-sm md:text-base text-destructive shrink-0 ml-2">
                            {formatCurrency(category.total)}
                          </span>
                        </div>
                      ))}
                      {monthlyReport.expenses.byCategory.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No expense data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Net Worth Report */}
          {activeTab === "net-worth" && netWorthReport && (
            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">{t("reports.netWorthReport")}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportNetWorthReportToPDF(netWorthReport)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t("reports.exportPDF")}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-chart-3/10 flex items-center justify-center shrink-0">
                        <BarChart3 className="h-5 w-5 text-chart-3" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("dashboard.netWorth")}</p>
                        <p className="text-xl font-bold tabular-nums font-mono">
                          {formatCurrency(netWorthReport.current.netWorth)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <TrendingUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("netWorth.totalAssets")}</p>
                        <p className="text-xl font-bold text-primary tabular-nums font-mono">
                          {formatCurrency(netWorthReport.current.totalAssets)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                        <TrendingDown className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("netWorth.totalDebts")}</p>
                        <p className="text-xl font-bold text-destructive tabular-nums font-mono">
                          {formatCurrency(netWorthReport.current.totalDebts)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Change Summary */}
              {netWorthReport.change && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Change Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground mb-1">Net Worth Change</p>
                        <p className={`text-lg font-bold tabular-nums font-mono ${netWorthReport.change.netWorth >= 0 ? "text-primary" : "text-destructive"}`}>
                          {netWorthReport.change.netWorth >= 0 ? "+" : ""}{formatCurrency(netWorthReport.change.netWorth)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground mb-1">Assets Change</p>
                        <p className={`text-lg font-bold tabular-nums font-mono ${netWorthReport.change.assets >= 0 ? "text-primary" : "text-destructive"}`}>
                          {netWorthReport.change.assets >= 0 ? "+" : ""}{formatCurrency(netWorthReport.change.assets)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground mb-1">Debts Change</p>
                        <p className={`text-lg font-bold tabular-nums font-mono ${netWorthReport.change.debts <= 0 ? "text-primary" : "text-destructive"}`}>
                          {netWorthReport.change.debts >= 0 ? "+" : ""}{formatCurrency(netWorthReport.change.debts)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Income vs Expenses Report */}
          {activeTab === "income-expense" && incomeExpenseReport && (
            <div className="space-y-6">
              {/* Controls */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        type="date"
                        value={ieStartDate}
                        onChange={(e) => setIeStartDate(e.target.value)}
                        className="flex-1 min-w-0"
                      />
                      <span className="text-muted-foreground text-sm shrink-0">to</span>
                      <Input
                        type="date"
                        value={ieEndDate}
                        onChange={(e) => setIeEndDate(e.target.value)}
                        className="flex-1 min-w-0"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={fetchIncomeExpenseReport} className="flex-1 sm:flex-none">{t("reports.generate")}</Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportIncomeExpenseToCSV(incomeExpenseReport)}
                        className="flex-1 sm:flex-none"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">{t("reports.exportCSV")}</span>
                        <span className="sm:hidden">CSV</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <TrendingUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.totalIncome")}</p>
                        <p className="text-xl font-bold text-primary tabular-nums font-mono">
                          {formatCurrency(incomeExpenseReport.income.total)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                        <TrendingDown className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.totalExpenses")}</p>
                        <p className="text-xl font-bold text-destructive tabular-nums font-mono">
                          {formatCurrency(incomeExpenseReport.expenses.total)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-chart-2/10 flex items-center justify-center shrink-0">
                        <PiggyBank className="h-5 w-5 text-chart-2" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.savingsRate")}</p>
                        <p className="text-xl font-bold tabular-nums font-mono">
                          {incomeExpenseReport.comparison.savingsRate.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Category Breakdown Report */}
          {activeTab === "category-breakdown" && categoryReport && (
            <div className="space-y-6">
              {/* Controls */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <Input
                        type="date"
                        value={cbStartDate}
                        onChange={(e) => setCbStartDate(e.target.value)}
                        className="flex-1 min-w-0"
                      />
                      <span className="text-muted-foreground text-sm shrink-0">to</span>
                      <Input
                        type="date"
                        value={cbEndDate}
                        onChange={(e) => setCbEndDate(e.target.value)}
                        className="flex-1 min-w-0"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={fetchCategoryBreakdownReport} className="flex-1 sm:flex-none">{t("reports.generate")}</Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportCategoryBreakdownToCSV(categoryReport)}
                        className="flex-1 sm:flex-none"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">{t("reports.exportCSV")}</span>
                        <span className="sm:hidden">CSV</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <TrendingUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.totalIncome")}</p>
                        <p className="text-xl font-bold text-primary tabular-nums font-mono">
                          {formatCurrency(categoryReport.totals.income)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                        <TrendingDown className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.totalExpenses")}</p>
                        <p className="text-xl font-bold text-destructive tabular-nums font-mono">
                          {formatCurrency(categoryReport.totals.expenses)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-chart-2/10 flex items-center justify-center shrink-0">
                        <PiggyBank className="h-5 w-5 text-chart-2" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("reports.savings")}</p>
                        <p className="text-xl font-bold tabular-nums font-mono">
                          {formatCurrency(categoryReport.totals.savings)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Breakdown Details */}
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                      Income by Source
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {categoryReport.incomeBySource.map((source) => (
                        <div key={source.sourceName} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                          <div>
                            <p className="font-medium">{source.sourceName}</p>
                            <p className="text-xs text-muted-foreground">{source.percentage.toFixed(1)}% of total</p>
                          </div>
                          <span className="font-medium tabular-nums font-mono text-primary">
                            {formatCurrency(source.total)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-destructive" />
                      Expenses by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {categoryReport.expensesByCategory.map((category) => (
                        <div key={category.category} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                          <div>
                            <p className="font-medium">{category.category}</p>
                            <p className="text-xs text-muted-foreground">{category.percentage.toFixed(1)}% of total</p>
                          </div>
                          <span className="font-medium tabular-nums font-mono text-destructive">
                            {formatCurrency(category.total)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}
